import type { CSSProperties } from 'react';

/** Shared money typography; each surface owns its font size and weight. */
export const MONO_NUMERIC_STYLE: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Warm Precision (spec D) shared list styles.
 *
 * Radius scale is the owner-locked premium set (S6 / M10 / L14), mirrored by
 * the antd theme tokens: buttons and inputs carry M10, list surfaces L14.
 * Controls are 28px tall; icon-only actions get a 32×32 hit area.
 */

/** Spec D `.btn`: 28px height, M10 radius, 12px/550 label. */
export const LIST_CREATE_BUTTON_STYLE: CSSProperties = {
  borderRadius: 10,
  height: 28,
  padding: '0 10px',
  fontSize: 12,
  fontWeight: 550,
};

/** Spec D `.icon-btn`: square icon action (reload / columns). */
export const LIST_ICON_BUTTON_STYLE: CSSProperties = {
  borderRadius: 10,
  height: 32,
  width: 32,
};

/** Spec D `.panel`: hairline surface with the L14 radius for list containers. */
export const LIST_SURFACE_STYLE: CSSProperties = {
  background: 'var(--ant-color-bg-container)',
  border: '1px solid var(--ant-color-border-secondary)',
  borderRadius: 'var(--ant-border-radius-lg, 14px)',
  overflow: 'hidden',
};

export const ENTITY_NAME_LINK_STYLE: CSSProperties = {
  fontWeight: 500,
  color: 'var(--ant-color-primary)',
};

export function listCountStyle(count: number): CSSProperties {
  return {
    backgroundColor: count > 0 ? 'var(--ant-color-primary-bg)' : 'var(--g-fill)',
    color: count > 0 ? 'var(--ant-color-primary)' : 'var(--ant-color-text-tertiary)',
    boxShadow: 'none',
    border: `1px solid ${count > 0 ? 'var(--ant-color-primary-border)' : 'var(--ant-color-border)'}`,
    fontFamily: 'inherit',
    fontVariantNumeric: 'tabular-nums',
  };
}

export const TABLE_SORTER_TOOLTIP = { target: 'sorter-icon' } as const;

/** Spec D `.seg`: strong-border track; the container clips option corners. */
export const SEG_TRACK_STYLE: CSSProperties = {
  background: 'var(--ant-color-bg-container)',
  border: '1px solid var(--ant-color-border)',
  borderRadius: 'var(--ant-border-radius, 10px)',
};

/**
 * Spec D `.seg button`: hairline inner dividers, soft-amber active pill with
 * 550 weight accent text, tertiary inactive icons.
 */
export function segOptionStyle(isActive: boolean, isFirst: boolean): CSSProperties {
  return {
    minWidth: 32,
    height: 26,
    padding: '0 8px',
    borderRadius: 0,
    backgroundColor: isActive ? 'var(--ant-color-primary-bg)' : 'transparent',
    color: isActive ? 'var(--ant-color-primary-text)' : 'var(--ant-color-text-tertiary)',
    fontWeight: isActive ? 550 : 500,
    boxShadow: 'none',
    ...(isFirst ? {} : { borderLeft: '1px solid var(--ant-color-border-secondary)' }),
  };
}
